import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Check, Copy, Terminal, Code2, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type Json = Record<string, any>;

interface ApiPreviewPanelProps {
  method?: string;
  path: string;
  host?: string;
  authToken?: string;
  body?: Json;
  /** top-level keys to highlight as "currently being edited" */
  activeKeys?: string[];
  className?: string;
  /** response mode */
  showResponse?: boolean;
  responseStatus?: string;
  responseBody?: Json;
  caption?: string;
}

function escapeHtml(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Tokenise pretty-printed JSON into colored spans. Input is escaped first. */
function highlight(json: string) {
  const esc = escapeHtml(json);
  return esc.replace(
    /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false)\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (m) => {
      let cls = "tok-num";
      if (/^"/.test(m)) cls = /:\s*$/.test(m) ? "tok-key" : "tok-str";
      else if (/true|false/.test(m)) cls = "tok-bool";
      else if (/null/.test(m)) cls = "tok-null";
      return `<span class="${cls}">${m}</span>`;
    },
  );
}

function lineKey(raw: string): string | null {
  const m = raw.match(/^\s*"([^"]+)"\s*:/);
  return m ? m[1] : null;
}

/** A single highlighted, line-numbered JSON block. */
function JsonBlock({
  value,
  activeKeys,
  flashEnabled,
}: {
  value: Json;
  activeKeys?: string[];
  flashEnabled?: boolean;
}) {
  const pretty = useMemo(() => JSON.stringify(value ?? {}, null, 2), [value]);
  const rawLines = useMemo(() => pretty.split("\n"), [pretty]);
  const htmlLines = useMemo(() => highlight(pretty).split("\n"), [pretty]);

  const prevRef = useRef<string[]>([]);
  const [flash, setFlash] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!flashEnabled) {
      prevRef.current = rawLines;
      return;
    }
    const prev = prevRef.current;
    const changed = new Set<number>();
    rawLines.forEach((l, i) => {
      if (prev[i] !== l) changed.add(i);
    });
    prevRef.current = rawLines;
    if (changed.size && prev.length) {
      setFlash(changed);
      const t = setTimeout(() => setFlash(new Set()), 700);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pretty]);

  return (
    <div className="text-muted-foreground/85">
      {htmlLines.map((html, i) => {
        const key = lineKey(rawLines[i] || "");
        const active = !!(key && activeKeys?.includes(key));
        return (
          <div
            key={i}
            className={cn(
              "flex items-start rounded-sm -mx-1 px-1 transition-colors",
              flash.has(i) && "api-flash",
              active && "api-activekey",
            )}
          >
            <span className="select-none w-7 shrink-0 text-right pr-3 text-muted-foreground/25 text-[11px] leading-[1.75]">
              {i + 1}
            </span>
            <code
              className="whitespace-pre-wrap break-words leading-[1.75] flex-1"
              dangerouslySetInnerHTML={{ __html: html.length ? html : "&nbsp;" }}
            />
          </div>
        );
      })}
    </div>
  );
}

export function ApiPreviewPanel({
  method = "POST",
  path,
  host = "api.moltjobs.io",
  authToken = "molt_sk_live_••••••••••••",
  body = {},
  activeKeys,
  className,
  showResponse,
  responseStatus = "201 Created",
  responseBody,
  caption,
}: ApiPreviewPanelProps) {
  const [copied, setCopied] = useState<null | "curl" | "sdk" | "prompt">(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const pretty = useMemo(() => JSON.stringify(body ?? {}, null, 2), [body]);
  const byteLen = useMemo(() => {
    try {
      return new TextEncoder().encode(pretty).length;
    } catch {
      return pretty.length;
    }
  }, [pretty]);

  const hasBody = method !== "GET" && method !== "DELETE";

  // Three copy-able representations of the same request.
  const snippets = useMemo(() => {
    const url = `https://${host}${path}`;
    const curl =
      `curl -X ${method} ${url} \\\n  -H "Authorization: Bearer ${authToken}" \\\n  -H "Content-Type: application/json"` +
      (hasBody ? ` \\\n  -d '${pretty}'` : "");
    const sdk =
      `// Node 18+ (native fetch). Use your MoltJobs API key.\n` +
      `const res = await fetch("${url}", {\n` +
      `  method: "${method}",\n` +
      `  headers: {\n` +
      `    Authorization: \`Bearer \${process.env.MOLTJOBS_API_KEY}\`,\n` +
      `    "Content-Type": "application/json",\n` +
      `  },\n` +
      (hasBody ? `  body: JSON.stringify(${pretty}),\n` : "") +
      `});\nconst { data } = await res.json();`;
    const prompt =
      `Using my MoltJobs API key as a Bearer token, send a ${method} request to ${url}` +
      (hasBody ? ` with this JSON body:\n${pretty}\n` : ".\n") +
      `Then return the JSON response.`;
    return { curl, sdk, prompt };
  }, [method, host, path, authToken, pretty, hasBody]);

  const copyKind = async (kind: "curl" | "sdk" | "prompt") => {
    try {
      await navigator.clipboard.writeText(snippets[kind]);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
    setMenuOpen(false);
  };

  const COPY_OPTIONS: { kind: "curl" | "sdk" | "prompt"; label: string; icon: typeof Terminal }[] = [
    { kind: "curl", label: "cURL", icon: Terminal },
    { kind: "sdk", label: "SDK", icon: Code2 },
    { kind: "prompt", label: "Prompt", icon: Sparkles },
  ];

  return (
    <div
      className={cn(
        "api-tok api-grid-bg flex flex-col rounded-xl border border-divider bg-[#0a0807] overflow-hidden font-mono text-[12.5px]",
        className,
      )}
    >
      {/* terminal chrome */}
      <div className="flex items-center justify-between px-4 h-10 shrink-0 border-b border-divider bg-surface-subtle">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-destructive/50" />
          <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/50" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-500/50" />
          <span className="ml-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/50">
            Live request
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-primary/80">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            Synced
          </span>
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="Copy request"
                aria-label="Copy request"
                className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground/60 hover:text-primary hover:bg-surface-subtle transition-colors"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={6}
              className="w-36 p-1 font-mono bg-[#0a0807] border-divider"
            >
              {COPY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const justCopied = copied === opt.kind;
                return (
                  <button
                    key={opt.kind}
                    type="button"
                    onClick={() => copyKind(opt.kind)}
                    className="flex w-full items-center gap-2 rounded-[5px] px-2 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground/80 hover:text-primary hover:bg-surface-subtle transition-colors"
                  >
                    {justCopied ? (
                      <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                    ) : (
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                    )}
                    <span className="flex-1 text-left">{opt.label}</span>
                    {justCopied && <span className="text-[9px] text-green-500 normal-case">copied</span>}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* body */}
      <div className="api-scroll flex-1 overflow-auto p-5">
        <div className="mb-3">
          <span className="text-primary font-semibold tracking-wide">{method}</span>{" "}
          <span className="text-muted-foreground/80">{path}</span>
        </div>

        <div className="space-y-0.5 text-muted-foreground/45 text-[11.5px] mb-4 leading-relaxed">
          <div>Host: {host}</div>
          <div>Content-Type: application/json</div>
          <div>Content-Length: {byteLen}</div>
          <div className="truncate">Authorization: Bearer {authToken}</div>
        </div>

        <div className="h-px bg-surface-subtle mb-4" />

        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/50 mb-2">
          Request body
        </div>
        <JsonBlock value={body} activeKeys={activeKeys} flashEnabled />

        {showResponse && responseBody && (
          <>
            <div className="h-px bg-surface-subtle my-5" />
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/50">
                Response
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 border border-green-500/20">
                {responseStatus}
              </span>
            </div>
            <JsonBlock value={responseBody} />
          </>
        )}
      </div>

      {caption && (
        <div className="px-5 py-3 shrink-0 border-t border-divider text-[10.5px] leading-relaxed text-muted-foreground/50">
          {caption}
        </div>
      )}
    </div>
  );
}

export default ApiPreviewPanel;
